import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { DateTime } from 'luxon';
import ShopHourForm from './ShopHourForm';
import 'react-quill/dist/quill.snow.css';

const EditShopHourModal = ({ show, onHide, onUpdate, shopHour, token }) => {
  const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startTime: '',
    endTime: '',
    backgroundImageUrl: '',
    backgroundMobileImageUrl: '',
    backgroundImageBrightness: '',
    backgroundMobileImageBrightness: '',
    backgroundThemeOverride: '',
    backgroundMobileThemeOverride: '',
    shopLogoImageUrl: '',
    shopButtonImageUrl: '',
    shopDescriptionImageUrl: '',
    shopDescription: '',
    active: false,
    shopHoursPassed: false,
  });
  const [dateError, setDateError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show && shopHour) {
      // Convert ISO times to datetime-local format
      const startLocal = DateTime.fromISO(shopHour.startTime, { zone: 'utc' })
        .setZone(USER_TIMEZONE)
        .toFormat("yyyy-MM-dd'T'HH:mm");
      const endLocal = DateTime.fromISO(shopHour.endTime, { zone: 'utc' })
        .setZone(USER_TIMEZONE)
        .toFormat("yyyy-MM-dd'T'HH:mm");

      setFormData({
        name: shopHour.name || '',
        description: shopHour.description || '',
        startTime: startLocal,
        endTime: endLocal,
        backgroundImageUrl: shopHour.backgroundImageUrl || '',
        backgroundMobileImageUrl: shopHour.backgroundMobileImageUrl || '',
        backgroundImageBrightness:
          shopHour.backgroundImageBrightness !== undefined &&
          shopHour.backgroundImageBrightness !== null
            ? String(shopHour.backgroundImageBrightness)
            : '',
        backgroundMobileImageBrightness:
          shopHour.backgroundMobileImageBrightness !== undefined &&
          shopHour.backgroundMobileImageBrightness !== null
            ? String(shopHour.backgroundMobileImageBrightness)
            : '',
        backgroundThemeOverride: shopHour.backgroundThemeOverride ?? '',
        backgroundMobileThemeOverride: shopHour.backgroundMobileThemeOverride ?? '',
        shopLogoImageUrl: shopHour.shopLogoImageUrl || '',
        shopButtonImageUrl: shopHour.shopButtonImageUrl || '',
        shopDescriptionImageUrl: shopHour.shopDescriptionImageUrl || '',
        shopDescription: shopHour.shopDescription || '',
        active: shopHour.active || false,
        shopHoursPassed: shopHour.shopHoursPassed || false,
      });
      setDateError('');
      setIsSaving(false);
    }
  }, [show, shopHour, USER_TIMEZONE]);

  const validateDates = () => {
    if (formData.endTime <= formData.startTime) {
      setDateError('End time must be after start time.');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (field === 'startTime' || field === 'endTime') {
      setTimeout(validateDates, 0);
    }
  };

  const handleSubmit = async () => {
    if (!validateDates()) return;

    setIsSaving(true);
    try {
      // Convert local times back to UTC ISO format
      const startUTC = DateTime.fromISO(formData.startTime, { zone: USER_TIMEZONE })
        .toUTC()
        .toISO();
      const endUTC = DateTime.fromISO(formData.endTime, { zone: USER_TIMEZONE }).toUTC().toISO();

      const normalizeBrightness = value => {
        if (value === '' || value === null || value === undefined) {
          return null;
        }
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const normalizeTheme = value => (value ? value : null);

      const updateData = {
        ...formData,
        startTime: startUTC,
        endTime: endUTC,
        backgroundImageBrightness: normalizeBrightness(formData.backgroundImageBrightness),
        backgroundMobileImageBrightness: normalizeBrightness(
          formData.backgroundMobileImageBrightness
        ),
        backgroundThemeOverride: normalizeTheme(formData.backgroundThemeOverride),
        backgroundMobileThemeOverride: normalizeTheme(formData.backgroundMobileThemeOverride),
      };

      await onUpdate(shopHour.id, updateData);
      onHide();
    } catch (error) {
      console.error('Error updating shop hour:', error);
      alert('Failed to update shop hour. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const saveTemplate = () => {
    const templates = JSON.parse(localStorage.getItem('announcementTemplates') || '[]');
    const templateName = prompt('Enter template name:');
    if (templateName) {
      templates.push({
        name: templateName,
        description: formData.description,
        createdAt: new Date().toISOString(),
      });
      localStorage.setItem('announcementTemplates', JSON.stringify(templates));
      alert('Template saved!');
    }
  };

  const loadTemplate = () => {
    const templates = JSON.parse(localStorage.getItem('announcementTemplates') || '[]');
    if (templates.length === 0) {
      alert('No templates found');
      return;
    }
    const templateList = templates.map((t, i) => `${i + 1}. ${t.name}`).join('\n');
    const choice = prompt(`Select template:\n${templateList}\n\nEnter number:`);
    if (choice) {
      const index = parseInt(choice) - 1;
      if (templates[index]) {
        handleChange('description', templates[index].description);
        alert('Template loaded!');
      }
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered dialogClassName="shop-hour-modal">
      <Modal.Header closeButton>
        <Modal.Title>Edit Shop Hour</Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-4">
        <ShopHourForm
          formData={formData}
          onChange={handleChange}
          dateError={dateError}
          timezoneLabel={USER_TIMEZONE}
          onSaveTemplate={saveTemplate}
          onLoadTemplate={loadTemplate}
          showShopHoursPassedToggle
          token={token}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditShopHourModal;
